import { Plus, Trash2, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FIELD_TYPES, CHOICE_TYPES } from "@/lib/api";

const uid = () => `f_${Math.random().toString(36).slice(2, 9)}`;

export default function FormBuilder({ fields = [], onChange }) {
  const update = (id, patch) => onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const remove = (id) => onChange(fields.filter((f) => f.id !== id));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...fields, { id: uid(), label: "", type: "short_text", required: false, options: [] }]);

  return (
    <div className="space-y-3" data-testid="form-builder">
      {fields.map((f, i) => (
        <div key={f.id} data-testid="builder-field" className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <div className="flex items-start gap-2">
            <GripVertical className="mt-2.5 h-4 w-4 shrink-0 text-slate-300" />
            <div className="flex-1 space-y-2">
              <Input data-testid="field-label-input" placeholder="Question / label" value={f.label} onChange={(e) => update(f.id, { label: e.target.value })} className="bg-white font-medium" />
              <div className="flex flex-wrap items-center gap-2">
                <Select value={f.type} onValueChange={(v) => update(f.id, { type: v })}>
                  <SelectTrigger data-testid="field-type-select" className="h-9 w-44 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <Switch checked={!!f.required} onCheckedChange={(v) => update(f.id, { required: v })} data-testid="field-required-switch" /> Required
                </label>
                <div className="ml-auto flex items-center gap-0.5">
                  <button onClick={() => move(i, -1)} className="rounded p-1.5 text-slate-400 hover:bg-slate-200"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(i, 1)} className="rounded p-1.5 text-slate-400 hover:bg-slate-200"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(f.id)} data-testid="field-delete" className="rounded p-1.5 text-slate-400 hover:bg-red-100 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              {CHOICE_TYPES.includes(f.type) && (
                <Input
                  data-testid="field-options-input"
                  placeholder="Options, comma separated (e.g. Yes, No, N/A)"
                  value={(f.options || []).join(", ")}
                  onChange={(e) => update(f.id, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })}
                  className="bg-white text-sm"
                />
              )}
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} data-testid="add-field-button" className="w-full border-dashed">
        <Plus className="mr-1.5 h-4 w-4" /> Add question
      </Button>
    </div>
  );
}
